import React from 'react';
import { X, Scale, ShieldAlert, Database, Globe } from 'lucide-react';

const LegalModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.8)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '20px',
      maxWidth: '750px',
      width: '100%',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
      overflow: 'hidden',
      animation: 'modalIn 0.3s ease-out'
    },
    header: {
      padding: '24px',
      borderBottom: '1px solid #f1f5f9',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#f8fafc'
    },
    content: {
      padding: '32px',
      overflowY: 'auto',
      color: '#334155',
      lineHeight: '1.7',
      fontSize: '0.95rem'
    },
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '1.1rem',
      fontWeight: '700',
      color: '#1e293b',
      marginTop: '24px',
      marginBottom: '12px'
    },
    textBlock: {
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '12px',
      borderLeft: '4px solid #3b82f6',
      marginBottom: '20px'
    },
    sourceGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px',
      marginTop: '15px'
    },
    sourceItem: {
      padding: '12px',
      border: '1px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.85rem'
    },
    stravaItem: {
      padding: '12px',
      border: '1px solid #FC4C02',
      borderRadius: '10px',
      fontSize: '0.85rem',
      backgroundColor: '#fef7f5'
    },
    link: {
      color: '#2563eb',
      textDecoration: 'none',
      fontWeight: '600'
    },
    stravaLink: {
      color: '#FC4C02',
      textDecoration: 'none',
      fontWeight: '600'
    },
    footer: {
      padding: '20px',
      borderTop: '1px solid #f1f5f9',
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: '#f8fafc'
    },
    btn: {
      padding: '12px 40px',
      backgroundColor: '#2563eb',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      fontWeight: '700',
      fontSize: '1rem'
    },
    disclaimer: {
      fontSize: '0.8rem',
      color: '#64748b',
      fontStyle: 'italic',
      marginTop: '8px'
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Scale size={28} color="#2563eb" />
            <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '800' }}>⚖️ Avís Legal i Fonts</h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={28} color="#64748b" />
          </button>
        </div>

        <div style={styles.content}>
          <p style={{ marginTop: 0 }}>
            Aquesta aplicació genera anàlisis automatitzades per a ús informatiu i recreatiu. 
            <strong> L'usuari és l'únic responsable de la seva seguretat a la muntanya.</strong>
          </p>

          <div style={styles.sectionTitle}>
            <ShieldAlert size={20} color="#e11d48" /> 1. Responsabilitat i Seguretat
          </div>
          <div style={styles.textBlock}>
            <p style={{ margin: '0 0 10px 0' }}>
              <strong>Dades Orientatives:</strong> Les rutes, previsions meteorològiques i estratègies de temps són estimacions generades per IA. No substitueixen el judici propi ni l'experiència en el medi natural.
            </p>
            <p style={{ margin: 0 }}>
              <strong>Estat del Terreny:</strong> L'estat real dels camins pot variar a causa de la meteorologia, el pas del temps o talls de via que no constin a les bases de dades digitals.
            </p>
          </div>

          <div style={styles.sectionTitle}>
            <Database size={20} color="#2563eb" /> 2. Atribució de dades (Crèdits)
          </div>
          <p>Aquesta eina utilitza serveis de tercers sota les seves respectives llicències:</p>
          
          <div style={styles.sourceGrid}>
            <div style={styles.sourceItem}>
              <strong>🗺️ Mapes:</strong> © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" style={styles.link}>OpenStreetMap</a> contributors (ODbL).
            </div>
            <div style={styles.sourceItem}>
              <strong>🤖 IA:</strong> Models <a href="https://deepmind.google/technologies/gemini/" target="_blank" rel="noopener noreferrer" style={styles.link}>Google Gemini</a> i <a href="https://groq.com/" target="_blank" rel="noopener noreferrer" style={styles.link}>Llama (Groq)</a>.
            </div>
            
            {/* Strava amb estil destacat i disclaimer */}
            <div style={{ ...styles.stravaItem, gridColumn: 'span 2' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#FC4C02" d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0L0 17.944h6.121"/>
                </svg>
                <strong>Segments:</strong>
              </div>
              <p style={{ margin: 0 }}>
                Powered by <a href="https://www.strava.com" target="_blank" rel="noopener noreferrer" style={styles.stravaLink}>Strava</a>. 
                Dades de segments obtingudes via l'API de Strava d'acord amb el{' '}
                <a href="https://www.strava.com/legal/api" target="_blank" rel="noopener noreferrer" style={styles.stravaLink}>
                  Strava API Agreement
                </a>.
              </p>
              <p style={styles.disclaimer}>
                Aquesta aplicació no està afiliada, patrocinada ni avalada per Strava, Inc.
              </p>
            </div>
            
            <div style={styles.sourceItem}>
              <strong>📚 Cultura:</strong> Informació de <a href="https://wikipedia.org" target="_blank" rel="noopener noreferrer" style={styles.link}>Wikipedia</a> (CC-BY-SA).
            </div>
            <div style={styles.sourceItem}>
              <strong>🌤️ Meteo:</strong> Previsions via <a href="https://open-meteo.com" target="_blank" rel="noopener noreferrer" style={styles.link}>Open-Meteo</a>.
            </div>
          </div>

          <div style={styles.sectionTitle}>
            <Globe size={20} color="#059669" /> 3. Sense Garanties
          </div>
          <p style={{ marginBottom: '30px' }}>
            L'aplicació no garanteix la precisió absoluta de les dades d'elevació, distància o superfícies. L'ús de les rutes es fa sota el propi risc de l'usuari.
          </p>
        </div>

        <div style={styles.footer}>
          <button style={styles.btn} onClick={onClose}>Accepto i comprenc els termes</button>
        </div>
      </div>
    </div>
  );
};

export default LegalModal;